import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SiMeta } from 'react-icons/si';
import { useAppState } from '~/stores/app-state';
import { useAuth } from '~/contexts/AuthContext';
import { useConnections, useConnectionStatus } from '~/hooks/useConnections';
import { useLatestReport } from '~/hooks/useReport';
import { FindingCard } from '~/components/app/components/FindingCard';
import { ActionCard } from '~/components/app/components/ActionCard';
import { RawDataModal } from '~/components/app/components/RawDataModal';
import ConnectionsDropdown from '~/components/app/components/ConnectionsDropdown';
import { DisconnectConnectionModal } from '~/components/app/components/DisconnectConnectionModal';
import MetaAdsConnectorModal from '~/components/app/components/MetaAdsConnectorModal';
import SetupWizard from '~/components/app/components/SetupWizard';
import WaitingStateCard from '~/components/app/components/WaitingStateCard';
import { getRequestWithAuth } from '~/lib/api';
import { deleteDatabaseConnection } from '~/lib/network/database-connection';
import { generatePartnerReport } from '~/lib/network/funnel-report';
import type { RecipeMetrics, Finding, Action } from '~/lib/network/funnel-report';

type TabType = 'findings' | 'actions';

// Severity ordering: positive (0) → critical (1) → warning (2)
const SEVERITY_ORDER: Record<string, number> = {
  positive: 0,
  critical: 1,
  warning: 2,
};

// Effort ordering: Quick win (0) → Medium effort (1) → Major project (2)
const EFFORT_ORDER: Record<string, number> = {
  'Quick win': 0,
  'Medium effort': 1,
  'Major project': 2,
};

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
}

export default function Dashboard() {
  const { refreshAuth } = useAuth();
  const { toggleChat } = useAppState();
  const { data: connections, isPending: connectionsLoading } = useConnections();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Local state
  const [activeTab, setActiveTab] = useState<TabType>('findings');
  const [showMetaModal, setShowMetaModal] = useState(false);
  const [reconnectError, setReconnectError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [rawDataModal, setRawDataModal] = useState<{
    isOpen: boolean;
    metrics: RecipeMetrics | null;
  }>({ isOpen: false, metrics: null });
  const [disconnectModal, setDisconnectModal] = useState<{
    isOpen: boolean;
    type: 'shopify' | 'meta_ads' | null;
  }>({ isOpen: false, type: null });

  // URL param detection for post-OAuth flow (clear it once processed)
  const isNewlyConnected = searchParams.get('newConnection') === 'true';

  // Extract Shopify connection (check both types for backward compatibility with legacy connections)
  const shopify = connections?.find(
    (c) => c.connection_type === 'shopify_oauth' || c.connection_type === 'shopify'
  );
  const metaAds = connections?.find((c) => c.connection_type === 'meta_ads');

  // Poll Shopify connection status if sync is in progress
  const isShopifySyncActive =
    shopify?.sync_status === 'PENDING' || shopify?.sync_status === 'IN_SYNC';
  const { data: shopifyStatus } = useConnectionStatus(
    shopify?._id ?? '',
    isShopifySyncActive && !!shopify?._id
  );
  const shopifySyncStatus = shopifyStatus?.sync_status ?? shopify?.sync_status;

  // Poll Meta connection status if sync is in progress
  const isMetaSyncActive =
    metaAds?.sync_status === 'PENDING' || metaAds?.sync_status === 'IN_SYNC';
  const { data: metaStatus } = useConnectionStatus(
    metaAds?._id ?? '',
    isMetaSyncActive && !!metaAds?._id
  );
  const metaSyncStatus = metaStatus?.sync_status ?? metaAds?.sync_status;

  // Fetch latest report (polling handles the wait)
  const { data: latestReport } = useLatestReport(shopify?._id, {
    enabled: !!shopify?._id,
  });

  // Derive state (latestReport can be null if no report exists yet)
  // PENDING = waiting for sync, GENERATING = sync done, report being generated
  const isGenerating = latestReport?.status === 'GENERATING';
  const hasCompletedReport = latestReport?.status === 'COMPLETED';
  const displayReport = hasCompletedReport ? latestReport : null;

  // Clear URL param after OAuth redirect is processed
  useEffect(() => {
    if (isNewlyConnected) {
      setSearchParams({}, { replace: true });
    }
  }, [isNewlyConnected, setSearchParams]);

  // Determine current step in the flow
  const determineStep = (): 'loading' | 'setup' | 'waiting' | 'ready' | 'error' => {
    // Check for OAuth return params - show setup to let wizard handle them
    const shopifyOAuthReturn = searchParams.get('shopifyOAuthReturn');
    const metaOAuthReturn = searchParams.get('metaOAuthReturn');
    if (shopifyOAuthReturn || metaOAuthReturn) {
      // Wait for connections to load so wizard receives existing connections as props
      if (connectionsLoading) return 'loading';
      return 'setup';
    }

    if (connectionsLoading) return 'loading';
    if (!latestReport) return 'setup';
    if (latestReport.status === 'FAILED') return 'error';
    if (latestReport.status !== 'COMPLETED') return 'waiting';
    return 'ready';
  };

  const currentStep = determineStep();

  // Handlers
  // Reconnect Shopify via OAuth (for ConnectionsDropdown)
  const handleReconnectShopify = async () => {
    setReconnectError(null);
    try {
      const { installUrl } = await getRequestWithAuth('/api/v1/shopify/installUrl');
      window.location.href = installUrl;
    } catch (error: any) {
      const message =
        error.response?.data?.detail || 'Failed to connect to Shopify. Please try again.';
      setReconnectError(message);
      console.error('Failed to get Shopify install URL:', error);
    }
  };

  // Generate report after wizard completion
  const handleGenerateReport = async (connectionId: string) => {
    try {
      await generatePartnerReport(connectionId);
      await queryClient.invalidateQueries({ queryKey: ['connections'] });
      await queryClient.invalidateQueries({ queryKey: ['latestReport', connectionId] });
      await refreshAuth();
      // The determineStep will now move to 'waiting' state automatically
    } catch (error) {
      console.error('Failed to generate report:', error);
      throw error;
    }
  };

  // Disconnect mutation for both connection types
  const disconnectMutation = useMutation({
    mutationFn: (connectionId: string) => deleteDatabaseConnection(connectionId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['connections'] });
      await refreshAuth();
      setDisconnectModal({ isOpen: false, type: null });
    },
  });

  const handleDisconnect = async () => {
    const connection = disconnectModal.type === 'shopify' ? shopify : metaAds;
    if (!connection?._id) return;
    await disconnectMutation.mutateAsync(connection._id);
  };

  // Extract findings and actions from presentations
  const getFindingsAndActions = () => {
    if (!displayReport?.presentations) {
      return { findings: [], actions: [] };
    }

    const presentations = displayReport.presentations;
    const findingsWithMeta: {
      finding: Finding;
      recipeName: string;
      metrics: RecipeMetrics;
    }[] = [];
    const actionsWithMeta: { action: Action; recipeName: string }[] = [];

    for (const [recipeName, presentation] of Object.entries(presentations)) {
      const recipeMetrics = displayReport.recipe_results.find(
        (r) => r.recipe_name === recipeName
      );

      if (presentation.finding) {
        findingsWithMeta.push({
          finding: presentation.finding,
          recipeName,
          metrics: recipeMetrics || {
            recipe_name: recipeName,
            success: false,
            metrics: {},
          },
        });
      }

      if (presentation.action) {
        actionsWithMeta.push({
          action: presentation.action,
          recipeName,
        });
      }
    }

    // Sort findings: positive → critical → warning
    findingsWithMeta.sort(
      (a, b) =>
        (SEVERITY_ORDER[a.finding.severity] ?? 99) -
        (SEVERITY_ORDER[b.finding.severity] ?? 99)
    );

    // Sort actions: Quick win → Medium effort → Major project
    actionsWithMeta.sort(
      (a, b) =>
        (EFFORT_ORDER[a.action.effort] ?? 99) -
        (EFFORT_ORDER[b.action.effort] ?? 99)
    );

    return { findings: findingsWithMeta, actions: actionsWithMeta };
  };

  const { findings, actions } = getFindingsAndActions();

  // STEP 1: Loading
  if (currentStep === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  // STEP 2: Setup - No Shopify connection or no report yet
  if (currentStep === 'setup') {
    return (
      <SetupWizard
        onComplete={handleGenerateReport}
        existingShopifyConnection={shopify}
        existingMetaConnection={metaAds}
      />
    );
  }

  // STEP 3: Waiting - Sync in progress or report generating
  if (currentStep === 'waiting') {
    return (
      <WaitingStateCard
        isGenerating={isGenerating}
        onOpenChat={toggleChat}
      />
    );
  }

  // STEP 3b: Error - Report generation failed
  if (currentStep === 'error') {
    const handleRetry = async () => {
      if (!shopify?._id) return;
      setIsRetrying(true);
      setRetryError(null);
      try {
        await generatePartnerReport(shopify._id);
        await queryClient.invalidateQueries({ queryKey: ['connections'] });
        await queryClient.invalidateQueries({ queryKey: ['latestReport', shopify._id] });
        // State will transition to 'waiting' on success
      } catch (error: any) {
        const message =
          error.response?.data?.detail || 'Failed to retry report generation. Please try again.';
        setRetryError(message);
        console.error(`Failed to retry report generation for connection ${shopify._id}:`, error);
      } finally {
        setIsRetrying(false);
      }
    };

    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="card bg-base-100 shadow-lg max-w-md w-full">
          <div className="card-body items-center">
            <h2 className="card-title text-error">Report Generation Failed</h2>
            <p className="text-base-content/70 mb-4">
              {retryError || latestReport?.error_message || 'Something went wrong while generating your report.'}
            </p>
            <button className="btn btn-primary" onClick={handleRetry} disabled={isRetrying}>
              {isRetrying ? (
                <>
                  <span className="loading loading-spinner loading-sm" />
                  Retrying...
                </>
              ) : (
                'Try Again'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // STEP 4: Dashboard Ready - Show report
  return (
    <div className="space-y-6">
      {/* Reconnect error alert */}
      {reconnectError && (
        <div className="alert alert-error mb-4">
          <span>{reconnectError}</span>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setReconnectError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Header with Meta badge (if connected) and connections dropdown */}
      <div className="flex justify-between items-start mb-4">
        <div className="space-y-2">
          {metaAds && (
            <div className="badge badge-outline gap-2">
              <SiMeta className="text-blue-600" />
              Meta Ads Connected
            </div>
          )}
        </div>
        <ConnectionsDropdown
          shopify={shopify}
          shopifySyncStatus={shopifySyncStatus}
          metaAds={metaAds}
          metaSyncStatus={metaSyncStatus}
          onAddShopify={handleReconnectShopify}
          onAddMeta={() => setShowMetaModal(true)}
          onDisconnectShopify={() => setDisconnectModal({ isOpen: true, type: 'shopify' })}
          onDisconnectMeta={() => setDisconnectModal({ isOpen: true, type: 'meta_ads' })}
        />
      </div>

      {/* Main Content - Report */}
      <div className="space-y-6">
        {/* Header */}
        <div className="text-sm text-base-content/60">
          Last {displayReport?.period_days} days
          {displayReport?.completed_at && (
            <span className="ml-1">
              • Updated {formatDate(displayReport.completed_at)}
            </span>
          )}
        </div>

        {/* Tabs */}
        <div role="tablist" className="tabs tabs-box">
          <button
            role="tab"
            className={`tab ${activeTab === 'findings' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('findings')}
          >
            Findings
          </button>
          <button
            role="tab"
            className={`tab ${activeTab === 'actions' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('actions')}
          >
            Actions {actions.length > 0 && `(${actions.length})`}
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'findings' && (
          <div className="space-y-3">
            {findings.length > 0 ? (
              findings.map(({ finding, recipeName, metrics }) => (
                <FindingCard
                  key={recipeName}
                  finding={finding}
                  recipeName={recipeName}
                  metrics={metrics}
                  onViewRawData={(m) =>
                    setRawDataModal({ isOpen: true, metrics: m })
                  }
                />
              ))
            ) : (
              <div className="text-center py-12 text-base-content/60">
                No insights available
              </div>
            )}
          </div>
        )}

        {activeTab === 'actions' && (
          <div className="space-y-3">
            {actions.length > 0 ? (
              actions.map(({ action, recipeName }) => (
                <ActionCard key={recipeName} action={action} recipeName={recipeName} />
              ))
            ) : (
              <div className="text-center py-12 text-base-content/60">
                No recommended actions right now. Your store is running smoothly.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Raw Data Modal */}
      <RawDataModal
        metrics={rawDataModal.metrics}
        isOpen={rawDataModal.isOpen}
        onClose={() => setRawDataModal({ isOpen: false, metrics: null })}
      />

      {/* Meta Ads Connector Modal */}
      <MetaAdsConnectorModal
        open={showMetaModal}
        onOpenChange={setShowMetaModal}
      />

      {/* Disconnect Connection Modal */}
      <DisconnectConnectionModal
        isOpen={disconnectModal.isOpen}
        connectionName={
          disconnectModal.type === 'shopify' ? shopify?.name ?? '' : metaAds?.name ?? ''
        }
        connectionType={disconnectModal.type ?? 'shopify'}
        onConfirm={handleDisconnect}
        onCancel={() => setDisconnectModal({ isOpen: false, type: null })}
      />
    </div>
  );
}
